import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
	Answer,
	HeaderButton,
	IconWrap,
	Inner,
	ItemCard,
	Panel,
	Question,
	Root,
} from "./frequently-asked-question-accordion.styles";
import { FrequentlyAskedQuestionAccordionProps } from "./frequently-asked-question-accordion.types";
import { ChevronIcon } from "./parts/svg-icons";
import { LMS_WEBAPP_CONFIG_IMPL } from "../../../../../infecto-lms-webapp-impl/config";

type AccordionItem = {
	id: string;
	question: string;
	answer: string;
	disabled?: boolean;
};

export function FrequentlyAskedQuestionAccordion({ items }: FrequentlyAskedQuestionAccordionProps) {
	const [openIds, setOpenIds] = useState<Set<string>>();

	const toggle = (id: string) => {
		setOpenIds((prev) => {
			const next = new Set(prev);
			const isOpen = next.has(id);
			next.clear();
			if (!isOpen) next.add(id);
			return next;
		});
	};

	return (
		<Root>
			{items.map((it: AccordionItem) => (
				<Row
					key={it.id}
					item={it}
					isOpen={openIds?.has(it.id) || false}
					onToggle={() => toggle(it.id)}
				/>
			))}
		</Root>
	);
}

function Row({
	item,
	isOpen,
	onToggle,
}: {
	item: AccordionItem;
	isOpen: boolean;
	onToggle: () => void;
}) {
	const uid = useId();
	const buttonId = `acc-btn-${uid}-${item.id}`;
	const panelId = `acc-panel-${uid}-${item.id}`;

	return (
		<ItemCard
			data-open={isOpen ? "true" : "false"}
			data-disabled={item.disabled ? "true" : "false"}
		>
			<HeaderButton
				id={buttonId}
				type="button"
				onClick={onToggle}
				disabled={item.disabled}
				aria-expanded={isOpen}
				aria-controls={panelId}
			>
				<Question dangerouslySetInnerHTML={{ __html: item.question }}></Question>
				<IconWrap aria-hidden="true" data-open={isOpen ? "true" : "false"}>
					<ChevronIcon color={LMS_WEBAPP_CONFIG_IMPL.brandColor} />
				</IconWrap>
			</HeaderButton>

			<AnimatedPanel id={panelId} aria-labelledby={buttonId} isOpen={isOpen}>
				<Answer className="mx-3 text-sm" dangerouslySetInnerHTML={{ __html: item.answer }}></Answer>
			</AnimatedPanel>
			<hr className="mx-5" />
		</ItemCard>
	);
}

function AnimatedPanel({
	isOpen,
	children,
	...rest
}: React.HTMLAttributes<HTMLDivElement> & { isOpen: boolean }) {
	const innerRef = useRef<HTMLDivElement | null>(null);
	const [height, setHeight] = useState(0);

	useLayoutEffect(() => {
		const el = innerRef.current;
		if (!el) return;
		setHeight(el.scrollHeight);
	}, [children, isOpen]);

	useEffect(() => {
		const el = innerRef.current;
		if (!el || typeof ResizeObserver === "undefined") return;

		const ro = new ResizeObserver(() => {
			setHeight(el.scrollHeight);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	return (
		<Panel
			role="region"
			data-open={isOpen ? "true" : "false"}
			style={{ height: isOpen ? height : 0 }}
			{...rest}
		>
			<Inner ref={innerRef} data-open={isOpen ? "true" : "false"}>
				{children}
			</Inner>
		</Panel>
	);
}
